import type {
  ApplicationVerifier,
  MultiFactorInfo,
  MultiFactorSession,
  TotpSecret,
} from 'firebase/auth';
import { firebaseEmulatorsEnabled } from '../config/env';
import { AppError, isAppError } from '../core/errors/AppError';
import { ERROR_CODES, USER_MESSAGE_KEYS } from '../core/errors/errorCodes';
import type { MfaResolver, MfaStatus, SmsEnrollmentSession, SmsMfaChallenge, TotpEnrollmentSession } from './authTypes';
import { getRecaptchaVerifier } from './recaptchaHelper';

function getAuthUser() {
  return import('../config/firebase.js').then(({ auth }) => auth.currentUser);
}

async function loadAuth() {
  const [{ auth }, authApi] = await Promise.all([
    import('../config/firebase.js'),
    import('firebase/auth'),
  ]);
  return { auth, ...authApi };
}

/** Auth emulator does not implement TOTP MFA secret generation. */
export function isTotpEnrollmentSupported() {
  return !firebaseEmulatorsEnabled();
}

function assertTotpEnrollmentSupported() {
  if (isTotpEnrollmentSupported()) return;
  throw new AppError({
    code: ERROR_CODES.AUTH_MFA_UNSUPPORTED,
    category: 'config',
    userMessageKey: USER_MESSAGE_KEYS.MFA_TOTP_EMULATOR_UNAVAILABLE,
    presentation: 'inline',
    severity: 'info',
  }, 'Auth emulator does not support TOTP MFA');
}

function rethrowTotpEnrollmentError(err: unknown): never {
  if (isAppError(err)) throw err;
  const code = (err as { code?: string })?.code;
  if (code === 'auth/requires-recent-login') {
    throw new AppError({
      code: ERROR_CODES.AUTH_SESSION_EXPIRED,
      category: 'auth',
      userMessageKey: USER_MESSAGE_KEYS.MFA_REQUIRES_RECENT_LOGIN,
      presentation: 'inline',
      severity: 'warn',
    }, 'Recent login required for MFA enrollment');
  }
  throw err;
}

export async function getMfaStatus(): Promise<MfaStatus> {
  const { multiFactor } = await import('firebase/auth');
  const user = await getAuthUser();
  if (!user) return { enrolled: false, factorCount: 0, totp: false, sms: false, email: false };

  const mf = multiFactor(user);
  const factors = mf?.enrolledFactors ?? [];
  let email = false;
  try {
    const { getEmailMfaStatus } = await import('./EmailMfaService');
    const emailStatus = await getEmailMfaStatus();
    email = emailStatus.enrolled;
  } catch {
    email = false;
  }

  return {
    enrolled: factors.length > 0 || email,
    factorCount: factors.length + (email ? 1 : 0),
    totp: factors.some((f) => f.factorId === 'totp'),
    sms: factors.some((f) => f.factorId === 'phone'),
    email,
  };
}

export async function startTotpEnrollment(): Promise<TotpEnrollmentSession> {
  assertTotpEnrollmentSupported();
  const { multiFactor, TotpMultiFactorGenerator } = await import('firebase/auth');
  const user = await getAuthUser();
  if (!user) throw new Error('Not signed in');

  try {
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    return {
      secret,
      secretKey: secret.secretKey,
      qrCodeUrl: secret.generateQrCodeUrl(user.email ?? 'user', 'Alea TUI'),
    };
  } catch (err) {
    rethrowTotpEnrollmentError(err);
  }
}

export async function finishTotpEnrollment(
  verificationCode: string,
  enrollment: TotpEnrollmentSession,
) {
  assertTotpEnrollmentSupported();
  const { multiFactor, TotpMultiFactorGenerator } = await import('firebase/auth');
  const user = await getAuthUser();
  if (!user) throw new Error('Not signed in');

  try {
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      enrollment.secret as TotpSecret,
      verificationCode,
    );
    await multiFactor(user).enroll(assertion, 'Authenticator');
    return getMfaStatus();
  } catch (err) {
    rethrowTotpEnrollmentError(err);
  }
}

export async function startSmsEnrollment(phoneNumber: string): Promise<SmsEnrollmentSession> {
  const { auth, multiFactor, PhoneAuthProvider } = await loadAuth();
  const user = await getAuthUser();
  if (!user) throw new Error('Not signed in');

  const session = await multiFactor(user).getSession();
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  const recaptchaVerifier = await getRecaptchaVerifier();
  const verificationId = await phoneAuthProvider.verifyPhoneNumber(
    { phoneNumber, session },
    recaptchaVerifier as ApplicationVerifier,
  );
  return { verificationId };
}

export async function finishSmsEnrollment(verificationId: string, verificationCode: string) {
  const { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator } = await loadAuth();
  const user = await getAuthUser();
  if (!user) throw new Error('Not signed in');

  const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
  const assertion = PhoneMultiFactorGenerator.assertion(credential);
  await multiFactor(user).enroll(assertion, 'SMS');
  return getMfaStatus();
}

export async function unenrollTotpMfa() {
  const { multiFactor } = await import('firebase/auth');
  const user = await getAuthUser();
  if (!user) throw new Error('Not signed in');
  const factors = multiFactor(user).enrolledFactors;
  const totp = factors.find((f) => f.factorId === 'totp');
  if (!totp) return getMfaStatus();
  await multiFactor(user).unenroll(totp);
  return getMfaStatus();
}

export async function unenrollSmsMfa() {
  const { multiFactor } = await import('firebase/auth');
  const user = await getAuthUser();
  if (!user) throw new Error('Not signed in');
  const factors = multiFactor(user).enrolledFactors;
  const phone = factors.find((f) => f.factorId === 'phone');
  if (!phone) return getMfaStatus();
  await multiFactor(user).unenroll(phone);
  return getMfaStatus();
}

export async function startSmsMfaSignIn(resolver: MfaResolver): Promise<SmsMfaChallenge> {
  const { auth, PhoneAuthProvider } = await loadAuth();
  const hint = resolver.hints.find((h) => h.factorId === 'phone') ?? resolver.hints[0];
  if (!hint) throw new Error('No MFA factor available');

  const phoneAuthProvider = new PhoneAuthProvider(auth);
  const recaptchaVerifier = await getRecaptchaVerifier();
  const verificationId = await phoneAuthProvider.verifyPhoneNumber(
    { multiFactorHint: hint as MultiFactorInfo, session: resolver.session as MultiFactorSession },
    recaptchaVerifier as ApplicationVerifier,
  );
  return { verificationId, factorId: hint.factorId };
}

export async function resolveTotpMfaSignIn(resolver: MfaResolver, verificationCode: string) {
  const { TotpMultiFactorGenerator } = await import('firebase/auth');
  const hint = resolver.hints.find((h) => h.factorId === 'totp') ?? resolver.hints[0];
  if (!hint) throw new Error('No MFA factor available');
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, verificationCode);
  const credential = await resolver.resolveSignIn(assertion);
  return credential.user;
}

export async function resolveSmsMfaSignIn(
  resolver: MfaResolver,
  verificationId: string,
  verificationCode: string,
) {
  const { PhoneAuthProvider, PhoneMultiFactorGenerator } = await import('firebase/auth');
  const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
  const assertion = PhoneMultiFactorGenerator.assertion(credential);
  const result = await resolver.resolveSignIn(assertion);
  return result.user;
}

export async function resolveMfaSignIn(resolver: MfaResolver, verificationCode: string) {
  const hint = resolver.hints.find((h) => h.factorId === 'totp') ?? resolver.hints[0];
  if (hint?.factorId === 'phone') {
    throw new Error('SMS MFA requires a verification session. Call startSmsMfaSignIn first.');
  }
  return resolveTotpMfaSignIn(resolver, verificationCode);
}
